import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  parseCompanyTableQuery,
  serializeCompanyTableQuery,
  type CompanyTableFilters,
  type CompanyTableQueryState,
} from '../lib/adminCompanies';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import type { AdminCompanyStatus } from '../types';
import type { AdminDataTableFilterValues, AdminDataTableSortState } from './components';
import { useAdminToast } from './components';
import CompanyDrawer from './CompanyDrawer';
import CompanyTable from './CompanyTable';
import { AdminRouteErrorStack } from './AdminShell';

type OpenDrawer = { mode: 'create' } | { mode: 'edit'; companyId: number };

const QueryKeys = ['name', 'domain', 'plan', 'country', 'status', 'sort'];

const normalizeFilters = (filters: AdminDataTableFilterValues): CompanyTableFilters => ({
  name: filters.name ?? '',
  domain: filters.domain ?? '',
  plan: filters.plan ?? '',
  country: filters.country ?? '',
  status: filters.status ?? '',
});

const useCompanyRouteStore = () => ({
  companies: useSuperAdminStore((state) => state.companies),
  companyDetail: useSuperAdminStore((state) => state.companyDetail),
  companyLoading: useSuperAdminStore((state) => state.companyLoading),
  companyError: useSuperAdminStore((state) => state.companyError),
  fetchCompanies: useSuperAdminStore((state) => state.fetchCompanies),
  loadCompanyDetail: useSuperAdminStore((state) => state.loadCompanyDetail),
  clearCompanyDetail: useSuperAdminStore((state) => state.clearCompanyDetail),
  createCompany: useSuperAdminStore((state) => state.createCompany),
  saveCompany: useSuperAdminStore((state) => state.saveCompany),
  deleteCompany: useSuperAdminStore((state) => state.deleteCompany),
  bulkCompanies: useSuperAdminStore((state) => state.bulkCompanies),
});

const useCompanyRouteDrawer = (
  store: ReturnType<typeof useCompanyRouteStore>,
  showToast: ReturnType<typeof useAdminToast>['showToast']
) => {
  const [drawer, setDrawer] = React.useState<OpenDrawer | null>(null);
  const detailRequest = React.useRef(0);
  const openEdit = async (companyId: number) => {
    const request = ++detailRequest.current;
    setDrawer({ mode: 'edit', companyId });
    try {
      await store.loadCompanyDetail(companyId);
    } catch (error) {
      if (request !== detailRequest.current) {
        return;
      }
      const failure = getAdminApiError(error, 'company_detail_failed');
      showToast(`Company details failed to load (${failure.code}).`, { tone: 'critical' });
      setDrawer(null);
      store.clearCompanyDetail();
    }
  };
  const close = () => {
    detailRequest.current += 1;
    setDrawer(null);
    store.clearCompanyDetail();
  };
  return {
    activeDetail: drawer?.mode === 'edit' && store.companyDetail?.id === drawer.companyId
      ? store.companyDetail
      : null,
    close,
    drawer,
    create: () => setDrawer({ mode: 'create' }),
    openEdit,
  };
};

const AdminCompaniesRoute: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useAdminToast();
  const store = useCompanyRouteStore();
  const drawerState = useCompanyRouteDrawer(store, showToast);
  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const queryState = React.useMemo(() => parseCompanyTableQuery(searchParams), [searchParams]);
  const { fetchCompanies } = store;

  React.useEffect(() => {
    void fetchCompanies();
  }, [fetchCompanies]);

  React.useEffect(() => {
    const availableIds = new Set(store.companies.map((company) => company.id));
    setSelectedIds((current) => current.filter((id) => availableIds.has(id)));
  }, [store.companies]);

  const writeQuery = (state: CompanyTableQueryState) => {
    const next = new URLSearchParams(searchParams);
    QueryKeys.forEach((key) => next.delete(key));
    serializeCompanyTableQuery(state).forEach((value, key) => next.set(key, value));
    setSearchParams(next, { replace: true });
  };

  const changeStatus = (companyId: number, status: AdminCompanyStatus) =>
    store.saveCompany(companyId, { status });

  const prepareBulkDelete = async (ids: number[]) => {
    const details = await Promise.all(ids.map((id) => store.loadCompanyDetail(id)));
    return Object.fromEntries(details.map((detail) => [detail.id, detail.deletePreview]));
  };
  const showCompanyError = Boolean(
    store.companyError && store.companies.length === 0 && drawerState.drawer === null
  );

  return (
    <>
      <AdminRouteErrorStack active={showCompanyError}>
        {showCompanyError && (
          <div className="admin-companies-error" role="alert">Unable to load companies: {store.companyError}</div>
        )}
        <CompanyTable
          companies={store.companies}
          detailById={(id) => store.companyDetail?.id === id ? store.companyDetail : null}
          onCreate={drawerState.create}
          onOpen={(company) => void drawerState.openEdit(company.id)}
          onBulkAction={store.bulkCompanies}
          onPrepareBulkDelete={prepareBulkDelete}
          filterValues={queryState.filters}
          onFilterValuesChange={(filters) => writeQuery({ filters: normalizeFilters(filters), sort: queryState.sort })}
          sort={queryState.sort as AdminDataTableSortState}
          onSortChange={(sort) => writeQuery({ filters: queryState.filters, sort })}
          selectedIds={selectedIds}
          onSelectedIdsChange={(ids) => setSelectedIds(ids.map(Number).filter(Number.isFinite))}
          busy={store.companyLoading}
        />
      </AdminRouteErrorStack>
      {drawerState.drawer && (
        <CompanyDrawer
          open
          mode={drawerState.drawer.mode}
          company={drawerState.activeDetail}
          loading={drawerState.drawer.mode === 'edit'
            && store.companyLoading
            && drawerState.activeDetail === null}
          onClose={drawerState.close}
          onCreate={store.createCompany}
          onSave={store.saveCompany}
          onDelete={store.deleteCompany}
          onChangeStatus={changeStatus}
        />
      )}
    </>
  );
};

export default AdminCompaniesRoute;
